import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Invoice, Customer, Vehicle, Appointment } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { formatCurrency } from "@/lib/utils";
import { formatRefDisplay } from "@/lib/shortId";
import {
  COMPANY_INFO,
  DOCUMENT_FOOTER_LINES,
  buildPaymentLineItem,
  formatPaymentMode,
  formatPaymentStatusLabel,
  getBillToDetails,
  getPaymentDocumentFileName,
  getPaymentDocumentNumber,
  getPaymentDocumentType,
} from "@/lib/paymentDocument";
import { format } from "date-fns";
import { WE_CLEAN_LOGO } from "@/assets/logo";

interface GenerateInvoicePdfParams {
  invoice: Invoice;
  customer: Customer;
  vehicles: Vehicle[];
  appointment?: Appointment;
}

export function generateInvoicePdf({
  invoice,
  customer,
  vehicles,
  appointment,
}: GenerateInvoicePdfParams): void {
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Colors
    const black: [number, number, number] = [0, 0, 0];
    const darkGray: [number, number, number] = [64, 64, 64];

    // Set font
    doc.setFont("helvetica");

    // --- Header Section ---
    // Logo (Top Left)
    const logoWidth = 50; // Adjusted size (approx 2:1 ratio)
    const logoHeight = 25;
    try {
      doc.addImage(
        WE_CLEAN_LOGO,
        "JPEG",
        margin,
        margin,
        logoWidth,
        logoHeight,
      );
    } catch (e) {
      console.error("Error adding logo:", e);
      // Fallback: text if image fails
      doc.setFontSize(10);
      doc.text("WeClean", margin, margin + 10);
    }

    // Invoice Title (Top Right)
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text("Invoice", pageWidth - margin, margin + 12, { align: "right" });

    // Invoice Details (Below Title, Right Aligned)
    yPosition = margin + 25; // Start below title
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);

    // Helper for right aligned text
    const rightAlignText = (text: string, y: number) => {
      doc.text(text, pageWidth - margin, y, { align: "right" });
    };

    rightAlignText(`Invoice number: ${invoice.invoiceNumber}`, yPosition);

    yPosition += 6;
    const issueDate = format(new Date(invoice.date), "MMMM dd, yyyy");
    rightAlignText(`Date of issue: ${issueDate}`, yPosition);

    yPosition += 6;
    rightAlignText(`Date due: ${issueDate}`, yPosition);

    // --- Info Section ---
    // Company Info (Left Side, Below Logo)
    yPosition = margin + 35; // Adjust based on logo size
    const leftColumnY = yPosition;

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(COMPANY_INFO.name, margin, yPosition);

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text(COMPANY_INFO.tagline, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.phone, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.email, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.website, margin, yPosition);

    // Bill To (Right Side, Below Invoice Details header area roughly)
    const rightColumnY = leftColumnY; // Start at same Y as company info
    yPosition = rightColumnY;

    // Header "Bill to:"
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text("Bill to:", pageWidth - margin, yPosition, { align: "right" });

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold"); // Bold for customer name
    doc.setTextColor(...black);
    doc.text(customer.name, pageWidth - margin, yPosition, { align: "right" });

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    if (customer.city) {
      doc.text(customer.city, pageWidth - margin, yPosition, {
        align: "right",
      });
      yPosition += 6;
    }
    if (customer.email) {
      doc.text(customer.email, pageWidth - margin, yPosition, {
        align: "right",
      });
      yPosition += 6;
    }

    // Ensure we start below the lowest column
    yPosition = Math.max(yPosition, leftColumnY + 30) + 10;

    // Amount due summary (Prominent, Left Aligned now as per standard, or keep custom?)
    // Let's keep it prominent but maybe left aligned looks better with the table below
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    const amountDueText = `${formatCurrency(invoice.total)} due ${issueDate}`;
    doc.text(amountDueText, margin, yPosition);

    // Line items table
    yPosition += 10;

    // Prepare table data
    const tableData = invoice.items.map((item) => {
      // Build description - first line is the item name
      const descriptionLines: string[] = [item.name];

      // Add description if available
      if (item.description) {
        descriptionLines.push(item.description);
      }

      // Add service period for subscriptions
      if (appointment && appointment.type === "USER_SUBSCRIPTION") {
        const startDate = format(new Date(appointment.date), "MMM dd");
        // Calculate end date (assuming monthly subscription)
        const endDate = new Date(appointment.date);
        endDate.setMonth(endDate.getMonth() + 1);
        const endDateFormatted = format(endDate, "MMM dd, yyyy");
        descriptionLines.push(
          `Service Period: ${startDate} - ${endDateFormatted}`,
        );
      }

      return [
        descriptionLines.join("\n"),
        item.quantity.toString(),
        formatCurrency(item.unitPrice),
        formatCurrency(item.total),
      ];
    });

    // Create table
    autoTable(doc, {
      startY: yPosition,
      head: [["Description", "Qty", "Unit price", "Amount"]],
      body: tableData,
      theme: "plain",
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontStyle: "bold",
        fontSize: 10,
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
        halign: "left",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontSize: 10,
        lineColor: [240, 240, 240],
        lineWidth: 0.3,
      },
      styles: {
        cellPadding: { top: 6, bottom: 6, left: 5, right: 5 },
        fontSize: 10,
        font: "helvetica",
        overflow: "linebreak",
        cellWidth: "wrap",
      },
      columnStyles: {
        0: { cellWidth: "auto", halign: "left", valign: "top" },
        1: { cellWidth: 18, halign: "center", valign: "middle" },
        2: { cellWidth: 32, halign: "right", valign: "middle" },
        3: { cellWidth: 32, halign: "right", valign: "middle" },
      },
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });

    // Get final Y position after table
    const finalY = (doc as any).lastAutoTable.finalY || yPosition + 50;

    // Total charges breakdown (right-aligned)
    let totalY = finalY + 12;
    const labelX = pageWidth - margin - 50;
    const valueX = pageWidth - margin;

    // Subtotal
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text("Subtotal:", labelX, totalY, { align: "right" });
    doc.setTextColor(...black);
    doc.text(formatCurrency(invoice.subtotal), valueX, totalY, {
      align: "right",
    });

    // Price adjustment (if any). Backend discount is inverted: positive
    // reduces the price, negative is a surcharge. Show both so the line
    // items reconcile with the total.
    if (invoice.discount !== 0) {
      totalY += 6;
      const isSurcharge = invoice.discount < 0;
      doc.setTextColor(...darkGray);
      doc.text(
        isSurcharge
          ? "Price Adjustment (Surcharge):"
          : "Price Adjustment (Discount):",
        labelX,
        totalY,
        { align: "right" },
      );
      doc.setTextColor(...black);
      doc.text(
        `${isSurcharge ? "+" : "-"}${formatCurrency(Math.abs(invoice.discount))}`,
        valueX,
        totalY,
        { align: "right" },
      );
    }

    // Platinum Member Discount (if any)
    if (invoice.platinumDiscount && invoice.platinumDiscount > 0) {
      totalY += 6;
      doc.setTextColor(...darkGray);
      doc.text("Platinum Member Discount (15%):", labelX, totalY, { align: "right" });
      doc.setTextColor(217, 119, 6); // Amber color for platinum
      doc.text(`-${formatCurrency(invoice.platinumDiscount)}`, valueX, totalY, {
        align: "right",
      });
    }

    // Tax (if any)
    if (invoice.tax > 0) {
      totalY += 6;
      doc.setTextColor(...darkGray);
      doc.text("Tax:", labelX, totalY, { align: "right" });
      doc.setTextColor(...black);
      doc.text(formatCurrency(invoice.tax), valueX, totalY, { align: "right" });
    }

    // Total
    totalY += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text("Total:", labelX, totalY, { align: "right" });
    doc.text(formatCurrency(invoice.total), valueX, totalY, { align: "right" });

    // Amount due
    totalY += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text("Amount due:", labelX, totalY, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(formatCurrency(invoice.total), valueX, totalY, { align: "right" });

    // Save PDF
    const fileName = `Invoice-${invoice.invoiceNumber}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("Error generating invoice PDF:", error);
    throw new Error("Failed to generate invoice PDF");
  }
}

interface GeneratePaymentReceiptPdfParams {
  payment: PaymentOutput;
  customer?: Customer | null;
  vehicles?: Vehicle[];
}

export function generatePaymentReceiptPdf({
  payment,
  customer,
  vehicles = [],
}: GeneratePaymentReceiptPdfParams): void {
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const rightEdge = pageWidth - margin;
    let yPosition = margin;

    // Colors
    const black: [number, number, number] = [0, 0, 0];
    const darkGray: [number, number, number] = [64, 64, 64];

    const documentType = getPaymentDocumentType(payment);
    const isReceipt = documentType === "Receipt";
    const documentNumber = getPaymentDocumentNumber(payment);

    // Set font
    doc.setFont("helvetica");

    // --- Header Section ---
    // Logo only (Top Left) - it already carries the brand name and tagline
    const logoWidth = 50; // Adjusted size (approx 2:1 ratio)
    const logoHeight = 25;
    try {
      doc.addImage(
        WE_CLEAN_LOGO,
        "JPEG",
        margin,
        margin,
        logoWidth,
        logoHeight,
      );
    } catch (e) {
      console.error("Error adding logo:", e);
      // Fallback
      doc.setFontSize(10);
      doc.text("WeClean", margin, margin + 10);
    }

    // Title (Top Right): Receipt only once fully paid, otherwise Invoice
    doc.setFontSize(26);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(documentType, rightEdge, margin + 10, { align: "right" });

    // Document number and date (below title, right aligned)
    const drawRightAlignedPair = (label: string, value: string, y: number) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...black);
      doc.text(value, rightEdge, y, { align: "right" });
      const valueWidth = doc.getTextWidth(value);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkGray);
      doc.text(label, rightEdge - valueWidth - 2, y, { align: "right" });
    };

    doc.setFontSize(10);
    drawRightAlignedPair(`${documentType} No:`, documentNumber, margin + 19);

    const displayDateSource =
      payment.refType === "APPOINTMENT" && payment.appointmentScheduledAt
        ? payment.appointmentScheduledAt
        : payment.date;
    if (displayDateSource) {
      drawRightAlignedPair(
        "Date:",
        format(new Date(displayDateSource), "MMMM dd, yyyy"),
        margin + 25,
      );
    }

    // --- Below the logo: Bill To (left column) and Payment details (right column) ---
    const sectionTopY = margin + 40;
    const rightColumnX = pageWidth / 2 + 5;

    const drawSectionHeading = (heading: string, x: number) => {
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...darkGray);
      doc.text(heading, x, sectionTopY);
    };

    // Bill To: name in bold, then whichever contact details the customer has
    let billToY = sectionTopY;
    const billTo = getBillToDetails(payment, customer);
    if (billTo) {
      const billToWidth = rightColumnX - margin - 10;
      drawSectionHeading("BILL TO", margin);

      billToY += 6;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...black);
      const nameLines: string[] = doc.splitTextToSize(billTo.name, billToWidth);
      doc.text(nameLines, margin, billToY);
      billToY += (nameLines.length - 1) * 5;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkGray);
      for (const contactLine of billTo.contactLines) {
        const wrappedLines: string[] = doc.splitTextToSize(contactLine, billToWidth);
        billToY += 5;
        doc.text(wrappedLines, margin, billToY);
        billToY += (wrappedLines.length - 1) * 5;
      }
    }

    // Payment details as aligned label/value rows
    const detailsValueX = rightColumnX + 30;
    let detailsY = sectionTopY;
    drawSectionHeading("PAYMENT DETAILS", rightColumnX);

    const drawDetailRow = (label: string, value: string) => {
      detailsY += 6;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkGray);
      doc.text(label, rightColumnX, detailsY);
      doc.setTextColor(...black);
      doc.text(value, detailsValueX, detailsY);
    };

    drawDetailRow("Status:", formatPaymentStatusLabel(payment));
    drawDetailRow("Payment Mode:", formatPaymentMode(payment.mode));

    // The reference is only worth a row when it isn't already the document number
    const referenceDisplay = formatRefDisplay(payment.bookingRef, payment.refId);
    if (referenceDisplay !== documentNumber) {
      drawDetailRow("Reference:", referenceDisplay);
    }

    if (payment.refType === "APPOINTMENT" && payment.appointmentScheduledAt) {
      drawDetailRow(
        "Service Date:",
        format(new Date(payment.appointmentScheduledAt), "MMM dd, yyyy HH:mm"),
      );
    }

    yPosition = Math.max(billToY, detailsY) + 10;

    // --- Headline amount: what is still owed on an Invoice, what was paid on a Receipt ---
    const headlineHeight = 16;
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(margin, yPosition, rightEdge - margin, headlineHeight, 2, 2, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text(isReceipt ? "Amount Paid" : "Amount Due", margin + 5, yPosition + 10.5);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(
      formatCurrency(isReceipt ? payment.paidAmount : payment.dueAmount),
      rightEdge - 5,
      yPosition + 11,
      { align: "right" },
    );

    yPosition += headlineHeight + 8;

    // --- Line items ---
    const lineItem = buildPaymentLineItem(payment, vehicles);
    autoTable(doc, {
      startY: yPosition,
      head: [["Description", "Qty", "Unit Price", "Amount"]],
      body: [
        [
          [lineItem.name, ...lineItem.details].join("\n"),
          lineItem.quantity.toString(),
          formatCurrency(lineItem.unitPrice),
          formatCurrency(lineItem.amount),
        ],
      ],
      theme: "grid",
      headStyles: {
        fillColor: [245, 245, 245],
        textColor: black,
        fontStyle: "bold",
        fontSize: 10,
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontSize: 10,
      },
      styles: {
        font: "helvetica",
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        lineColor: [225, 225, 225],
        lineWidth: 0.2,
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: "auto", halign: "left", valign: "top" },
        1: { cellWidth: 18, halign: "center", valign: "top" },
        2: { cellWidth: 34, halign: "right", valign: "top" },
        3: { cellWidth: 34, halign: "right", valign: "top" },
      },
      didParseCell: (data) => {
        // Keep header labels aligned with the numbers beneath them
        if (data.section === "head" && data.column.index === 1) {
          data.cell.styles.halign = "center";
        }
        if (data.section === "head" && data.column.index > 1) {
          data.cell.styles.halign = "right";
        }
      },
      margin: { left: margin, right: margin },
    });

    // --- Totals (right aligned) ---
    let totalsY = ((doc as any).lastAutoTable?.finalY ?? yPosition) + 8;
    const totalsLabelX = rightEdge - 40;

    const drawTotalRow = (label: string, value: string, emphasize = false) => {
      doc.setFontSize(emphasize ? 11 : 10);
      doc.setFont("helvetica", emphasize ? "bold" : "normal");
      doc.setTextColor(...(emphasize ? black : darkGray));
      doc.text(label, totalsLabelX, totalsY, { align: "right" });
      doc.setTextColor(...black);
      doc.text(value, rightEdge, totalsY, { align: "right" });
      totalsY += 6;
    };

    drawTotalRow("Subtotal:", formatCurrency(payment.totalAmount));

    // Backend discount is inverted: positive reduces the price, negative is a surcharge
    if (payment.discount !== 0) {
      const isSurcharge = payment.discount < 0;
      drawTotalRow(
        isSurcharge ? "Surcharge:" : "Discount:",
        `${isSurcharge ? "+" : "-"}${formatCurrency(Math.abs(payment.discount))}`,
      );
    }

    if (payment.platinumDiscount > 0) {
      drawTotalRow(
        "Platinum Discount (15%):",
        `-${formatCurrency(payment.platinumDiscount)}`,
      );
    }

    const netTotal = payment.totalAmount - payment.discount - payment.platinumDiscount;
    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(0.3);
    doc.line(totalsLabelX - 45, totalsY - 3.5, rightEdge, totalsY - 3.5);
    totalsY += 1;
    drawTotalRow("Total:", formatCurrency(netTotal), true);
    drawTotalRow("Paid Amount:", formatCurrency(payment.paidAmount));
    drawTotalRow("Due Amount:", formatCurrency(payment.dueAmount), true);

    // --- Footer: pinned to the page bottom, moved to a new page if content reaches it ---
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerHeight = 18;
    let footerY = pageHeight - margin - footerHeight;
    if (totalsY + 10 > footerY) {
      doc.addPage();
      footerY = pageHeight - margin - footerHeight;
    }

    doc.setDrawColor(...darkGray);
    doc.setLineWidth(0.4);
    doc.line(margin, footerY, rightEdge, footerY);

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(DOCUMENT_FOOTER_LINES[0], pageWidth / 2, footerY + 8, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text(DOCUMENT_FOOTER_LINES[1], pageWidth / 2, footerY + 14, { align: "center" });

    // Save PDF as "<Customer Name> - <Document Number>.pdf"
    doc.save(getPaymentDocumentFileName(payment, customer));
  } catch (error) {
    console.error("Error generating payment receipt PDF:", error);
    throw new Error("Failed to generate payment receipt PDF");
  }
}
